import React from 'react';

import {
  ActionIcon,
  Button,
  Checkbox,
  Group,
  Paper,
  Select,
  Skeleton,
  Space,
  Text,
  TextInput,
  Title,
  Tooltip,
  createStyles,
  useMantineTheme,
} from '@mantine/core';
import { useDebouncedValue, useMediaQuery } from '@mantine/hooks';
import { IconFilterOff, IconPlus, IconSearch, IconX } from '@tabler/icons-react';
import DownloadCSV from 'components/DownloadCSV';
import PackageMultiSelect from 'components/form-fields/auto-complete-fields/PackageMultiSelect';
import AthleteStatusSelect from 'components/form-fields/select-fields/AthleteStatusSelect';
import { useAuth } from 'context/auth-context';
import { useLocalization } from 'context/localization-context';
import useAthletes from 'hooks/athlete/useAthletes';
import { AthleteList } from 'types';
import { formatGymDate } from 'utils/date-functions';

const useStyles = createStyles(theme => ({
  header: {
    padding: theme.spacing.lg,
    background: theme.colors.gray[0],
    borderRadius: 0,
    borderBottom: `1px solid ${theme.colors.gray[3]}`,
    display: 'grid',
    gridTemplateColumns: 'max-content auto auto auto auto auto auto min-content',
    gridTemplateRows: 'auto auto',
    gridTemplateAreas: `
        'title title title title title title button button'
        'lastName search1 search2 search3 search4 search5 search6 actions'
      `,
    alignItems: 'center',
    gap: theme.spacing.sm,
    [theme.fn.smallerThan('lg')]: {
      gridTemplateColumns: 'max-content auto auto auto auto auto',
      gridTemplateRows: 'auto auto auto',
      gridTemplateAreas: `
        'title title title title button button'
        'search1 search2 search3 search4 search5 search6'
        'lastName . . . . actions'
      `,
    },
    [theme.fn.smallerThan('md')]: {
      gridTemplateColumns: 'max-content auto auto auto min-content min-content min-content',
      gridTemplateRows: 'auto auto auto auto',
      gridTemplateAreas: `
        'title title title title button button button'
        'search1 search1 search2 search2 search2 search2 search2'
        'search3 search4 search5 search5 search6 search6 search6'
        'lastName . . . . . actions'
      `,
    },
  },
  title: {
    gridArea: 'title',
  },
  lastName: {
    gridArea: 'lastName',
  },
  search1: {
    gridArea: 'search1',
    minWidth: '18rem',
  },
  search2: {
    gridArea: 'search2',
  },
  search3: {
    gridArea: 'search3',
  },
  search4: {
    gridArea: 'search4',
  },
  search5: {
    gridArea: 'search5',
  },
  search6: {
    gridArea: 'search6',
  },
  actions: {
    gridArea: 'actions',
  },
  button: {
    gridArea: 'button',
    width: 'min-content',
    marginLeft: 'auto',
  },
}));

interface FilterTerms {
  searchTerm: string;
  isArrangeByLastName: boolean;
  athleteStatus: string;
  loginEnabled: string;
  packages: string[];
  hasPaymentMethod: string;
  hasSignedWaiver: string;
  glmLinkState: string;
}

const csvOptions = {
  filename: 'athlete-list',
  fieldSeparator: ',',
  quoteStrings: '"',
  decimalSeparator: '.',
  showLabels: true,
  useBom: true,
  useKeysAsHeaders: true,
};

const AthletesPageHeader = ({
  searchFilters,
  orderingValue,
  initialSearchFiltersValue,
  setSearchFilters,
  handleOpenCreateForm,
  permission = true,
}: {
  searchFilters: FilterTerms;
  orderingValue: string;
  initialSearchFiltersValue: FilterTerms;
  setSearchFilters: React.Dispatch<React.SetStateAction<FilterTerms>>;
  handleOpenCreateForm: () => void;
  permission?: boolean;
}): JSX.Element => {
  const theme = useMantineTheme();
  const isDesktop = useMediaQuery(`(min-width: ${theme.breakpoints.sm}`, false);
  const { classes } = useStyles();
  const { dateFormat } = useLocalization();
  const { session } = useAuth();

  /* Debounce searchFilters for API call to match table's debounced filters
    This ensures React Query deduplicates the calls (same query key), so only 1 API call is made. */
  const [debouncedSearchFilters] = useDebouncedValue<FilterTerms>(searchFilters, 250);
  const { data, isLoading, isSuccess, isError } = useAthletes({
    ...debouncedSearchFilters,
    ordering: orderingValue,
  });

  const dataToRow = (athlete: AthleteList): { [key: string]: any } => ({
    Name: athlete.fullName || '',
    Status: athlete.athleteStatus || '',
    Login: athlete.loginEnabled ? 'True' : 'False',
    Email: athlete.email || '',
    Phone: athlete.phone || '',
    'Current packages':
      athlete.currentPackages && athlete.currentPackages.length > 0
        ? athlete.currentPackages.map(p => p.name).join(' and ')
        : '',
    'Has payment method': athlete.hasPaymentMethod ? 'True' : 'False',
    address: athlete.address?.addressLines || '',
    city: athlete.address?.locality || '',
    state: athlete.address?.region || '',
    zip: athlete.address?.zipCode || '',
    'Member Since': athlete.memberSince
      ? formatGymDate(athlete.memberSince, session?.gym?.timezone, dateFormat)
      : '',
  });

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const value = e.currentTarget?.value || '';
    setSearchFilters(prevValue => ({ ...prevValue, searchTerm: value }));
  };
  const handleIsArrangeByLastNameChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setSearchFilters(prevValue => ({ ...prevValue, isArrangeByLastName: !!e.currentTarget?.checked }));
  };
  const handleAthletePackageChange = (value: string[] | undefined): void => {
    setSearchFilters(prevValue => ({ ...prevValue, packages: value || [] }));
  };
  const handleAthleteStatusChange = (value: string | null): void => {
    setSearchFilters(prevValue => ({ ...prevValue, athleteStatus: value || '' }));
  };
  const handleAthleteLoginStatusChange = (value: string | null): void => {
    setSearchFilters(prevValue => ({ ...prevValue, loginEnabled: value || '' }));
  };
  const handleHasPaymentMethodChange = (value: string | null): void => {
    setSearchFilters(prevValue => ({ ...prevValue, hasPaymentMethod: value || '' }));
  };
  const handleHasSignedWaiverChange = (value: string | null): void => {
    setSearchFilters(prevValue => ({ ...prevValue, hasSignedWaiver: value || '' }));
  };
  const handleGLMLinkStateChange = (value: string | null): void => {
    setSearchFilters(prevValue => ({ ...prevValue, glmLinkState: value || '' }));
  };

  const handleClearFilters = (): void => {
    setSearchFilters(initialSearchFiltersValue);
  };
  const handleClearSearchFilter = (): void => {
    setSearchFilters(prevValue => ({ ...prevValue, searchTerm: '' }));
  };

  const getAthleteCount = (): JSX.Element => {
    if (isLoading) {
      return <Skeleton height='2rem' width='5rem' />;
    }
    if (isError) {
      return (
        <Title order={isDesktop ? 1 : 3} color='red'>
          Error
        </Title>
      );
    }
    if (isSuccess && data?.pages?.[0]?.data?.count !== undefined) {
      return (
        <Title order={isDesktop ? 1 : 3} color='gray'>
          {data.pages[0].data.count}
        </Title>
      );
    }
    return <></>;
  };

  const hookOptions = { ...searchFilters, ordering: orderingValue };

  return (
    <Paper className={classes.header}>
      <Group noWrap spacing='xl' className={classes.title}>
        <Title order={isDesktop ? 1 : 3}>Athletes</Title>
        {getAthleteCount()}
      </Group>
      <Checkbox
        className={classes.lastName}
        checked={searchFilters.isArrangeByLastName}
        onChange={handleIsArrangeByLastNameChange}
        label={
          <Text color='gray' fs='italic'>
            Arrange by last name
          </Text>
        }
      />

      <TextInput
        className={classes.search1}
        icon={<IconSearch size={theme.fontSizes.md} />}
        placeholder='Search by name, email, phone'
        value={searchFilters?.searchTerm}
        onChange={handleSearchChange}
        data-cy='athleteSearchInput'
        rightSection={
          !!searchFilters.searchTerm ? (
            <ActionIcon onClick={handleClearSearchFilter} size='md'>
              <IconX size={theme.fontSizes.md} />
            </ActionIcon>
          ) : (
            <Space />
          )
        }
      />

      <PackageMultiSelect
        className={classes.search2}
        placeholder='Select packages'
        value={searchFilters?.packages}
        onChange={handleAthletePackageChange}
      />

      <AthleteStatusSelect
        className={classes.search3}
        placeholder='Select status'
        value={searchFilters?.athleteStatus}
        onChange={handleAthleteStatusChange}
        clearable
      />

      <Select
        className={classes.search4}
        placeholder='Select login status'
        value={searchFilters?.loginEnabled}
        onChange={handleAthleteLoginStatusChange}
        data={[
          { label: 'Login Enabled', value: 'true' },
          { label: 'Login Disabled', value: 'false' },
        ]}
        clearable
      />
      <Select
        className={classes.search5}
        placeholder='Filter by payment methods'
        value={searchFilters?.hasPaymentMethod}
        onChange={handleHasPaymentMethodChange}
        data={[
          { label: 'Has payment method(s)', value: 'true' },
          { label: 'Has no payment methods', value: 'false' },
        ]}
        clearable
      />
      <Select
        className={classes.search6}
        placeholder='Filter by waiver'
        value={searchFilters?.hasSignedWaiver}
        onChange={handleHasSignedWaiverChange}
        data={[
          { label: 'Has waiver', value: 'true' },
          { label: 'Has no waiver', value: 'false' },
        ]}
        clearable
      />

      <Group className={classes.actions} noWrap>
        {session?.gym?.ghlV2Enabled && (
          <Select
            placeholder='GLM link status'
            value={searchFilters?.glmLinkState}
            onChange={handleGLMLinkStateChange}
            data={[
              { label: 'Linked', value: 'linked' },
              { label: 'Not linked', value: 'unlinked' },
              { label: 'Broken', value: 'broken' },
            ]}
            clearable
            w={160}
          />
        )}
        <Tooltip label='Clear filters'>
          <ActionIcon onClick={handleClearFilters}>
            <IconFilterOff />
          </ActionIcon>
        </Tooltip>

        <DownloadCSV
          useList={useAthletes}
          dataToRow={dataToRow}
          csvOptions={csvOptions}
          hookOptions={hookOptions}
          reportType='athletes-export'
        />
      </Group>

      <Button
        leftIcon={<IconPlus size={theme.fontSizes.md} />}
        onClick={handleOpenCreateForm}
        className={classes.button}
        disabled={!permission}
        data-cy='athleteAddButton'
      >
        Athlete
      </Button>
    </Paper>
  );
};

export default AthletesPageHeader;
